/**
 * / — public landing.
 *
 * V3 Desk + Hook layout from the design handoff:
 *   1. Marquee ticker tape (live, 6 tickers including VIX)
 *   2. Hero — split grid with brutalist headline + email capture
 *      on the left, live data panel mock on the right
 *   3. Feature strip — 3 columns (Heatmaps · Index Analogues · Risk)
 *   4. (Below the brief) How-it-works, proof, pricing, FAQ teasers
 *      preserved from the previous landing — they convert below the
 *      fold and didn't have a strong reason to disappear.
 *
 * The brutalist hero only fully renders under design="v2" (Bloomberg
 * Lab tokens). Under design="legacy" the same markup falls back to
 * the legacy chrome via the cascading tokens — readable, just less
 * opinionated. This keeps the legacy/v2 toggle non-destructive.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MarqueeTape from '../../components/landing/MarqueeTape';
import WaitlistForm from '../../components/landing/WaitlistForm';
import ProofLine, { Proof } from '../../components/login/ProofLine';
import PricingStrip from '../../components/login/PricingStrip';
import { usePageMeta } from '../../hooks/usePageMeta';
import { useReveal } from '../../hooks/useReveal';
import { API_BASE_URL } from '../../lib/constants';

const PROOF: Proof[] = [
  { text: 'I caught the SPY pin 14 minutes before it happened.',          cta: '— quant trader, NYC' },
  { text: 'Saved me from being long the day NDX flipped negative gamma.', cta: '— prop desk, Chicago' },
  { text: 'The air pocket map shows what dealers don\'t want you to see.', cta: '— independent vol trader' },
  { text: 'The model called the SPY $580 magnet — I scaled in early.',   cta: '— private investor, LA' },
];

const FAQ_TEASERS = [
  {
    q: 'What is GEX?',
    a: 'Gamma Exposure — how much underlying market makers need to buy or sell to stay delta-neutral as price moves. High positive GEX dampens moves; negative GEX amplifies them.',
  },
  {
    q: 'How fresh is the data?',
    a: 'We pull options chains every 30 seconds during market hours and stream the recomputed heatmap to your browser via WebSocket within ~200 ms.',
  },
  {
    q: 'Is this trading advice?',
    a: 'No. GammaVision is an analytical tool. Verify all numbers against your broker before acting on them.',
  },
];

const FEATURE_PILLARS = [
  {
    num: '01',
    tone: 'phosphor' as const,
    title: 'GEX HEATMAPS',
    body: 'Where dealer gamma sits across every strike. Reds = pressure to sell rallies. Greens = pressure to buy dips.',
  },
  {
    num: '02',
    tone: 'alert' as const,
    title: 'INDEX ANALOGUES',
    body: 'Pattern-match today against years of tape. See the closest historical sessions and what came next.',
  },
  {
    num: '03',
    tone: 'burn' as const,
    title: 'RISK MONITOR',
    body: 'Real-time alerts when net GEX flips, walls break, or dealer charm flow accelerates into close.',
  },
];

const Landing: React.FC = () => {
  usePageMeta(
    'GammaVision — Real-time options gamma & vanna heatmap',
    'Watch dealers hedge in real time. GEX & VEX heatmaps for SPX, NDX, SPY, QQQ, IWM. 7-day free trial.',
  );

  // Live waitlist count — fetched once on mount; shown only when >= 25.
  // Gracefully handles 404 (endpoint not yet live) and network failures.
  const [waitlistCount, setWaitlistCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/waitlist/count`)
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data: { count: number }) => {
        if (!cancelled && typeof data?.count === 'number' && data.count >= 25) {
          setWaitlistCount(data.count);
        }
      })
      .catch(() => { /* silently ignore — endpoint may not exist yet */ });
    return () => { cancelled = true; };
  }, []);

  const featuresR = useReveal<HTMLDivElement>();
  const timelineR = useReveal<HTMLDivElement>();
  const proofR    = useReveal<HTMLDivElement>();
  const pricingR  = useReveal<HTMLDivElement>();
  const faqR      = useReveal<HTMLDivElement>();

  return (
    <div className="gv-landing-v3">
      <MarqueeTape />

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="gv-hero-v3" aria-label="Hero">
        <div className="gv-hero-v3-left">
          <div className="gv-hero-eyebrow">[ GEX · VEX · 0DTE · ANALOGUES ]</div>
          <h1 className="gv-hero-headline">
            Watch dealers
            <br />
            <span className="gv-headline-accent">hedge in</span>
            <br />
            <span className="gv-headline-highlight">real time.</span>
          </h1>
          <p className="gv-hero-sub">
            $4.2T in dealer gamma exposure. Refreshed every 30 seconds.
            Mapped onto the price ladder for SPX, NDX, SPY, QQQ, IWM —
            so you stop guessing where the tape will pin.
          </p>
          <WaitlistForm />
          {waitlistCount !== null && (
            <div className="gv-funnel-count" aria-live="polite">
              {waitlistCount.toLocaleString()} traders waiting
            </div>
          )}
          <div className="gv-hero-actions">
            <Link to="/login" className="gv-cta-secondary">
              Already have an account? <strong>Sign in →</strong>
            </Link>
          </div>
        </div>

        {/* role="img" + aria-label lets SR users understand this is a decorative
            preview panel at a glance, then skip past it. The inner wrapper is
            aria-hidden so SR never reads the synthetic numbers (5832.50, BULLISH,
            etc.) that would confuse listeners expecting live prices.
            display:contents makes the wrapper invisible to the flex layout so its
            children are promoted to direct children of .gv-hero-v3-right, preserving
            the gap:14px spacing between panel-head, heatmap, and stat rows. */}
        <div className="gv-hero-v3-right" role="img" aria-label="Decorative chart preview">
          <div aria-hidden="true" style={{ display: 'contents' }}>
            <div className="gv-panel-head">
              <span>SPX · GEX HEATMAP · 0DTE</span>
              <span className="gv-panel-live">● LIVE</span>
            </div>
            <HeroHeatmapMock />
            <div className="gv-stat-row gv-stat-row--three">
              <div className="gv-stat">
                <div className="gv-stat-label">GAMMA FLIP</div>
                <div className="gv-stat-value">5832.50</div>
                <div className="gv-stat-delta gv-stat-delta--up">+12 pts</div>
              </div>
              <div className="gv-stat">
                <div className="gv-stat-label">CALL WALL</div>
                <div className="gv-stat-value">5870.00</div>
                <div className="gv-stat-delta gv-stat-delta--alert">+2.4B</div>
              </div>
              <div className="gv-stat">
                <div className="gv-stat-label">PUT WALL</div>
                <div className="gv-stat-value">5800.00</div>
                <div className="gv-stat-delta gv-stat-delta--down">-1.8B</div>
              </div>
            </div>
            <div className="gv-stat-row gv-stat-row--two">
              <div className="gv-stat gv-stat--spark">
                <div>
                  <div className="gv-stat-label">NDX · NET GEX</div>
                  <div className="gv-stat-spark-value gv-stat-delta--up">+$2.1B</div>
                </div>
                <Sparkline color="var(--gv-brand)" seed={0.5} />
              </div>
              <div className="gv-stat gv-stat--spark">
                <div>
                  <div className="gv-stat-label">SPY · 0DTE FLOW</div>
                  <div className="gv-stat-spark-value gv-stat-delta--alert">BULLISH</div>
                </div>
                <Sparkline color="var(--gv-pos)" seed={1.2} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature strip ─────────────────────────────────── */}
      <section
        ref={featuresR.ref}
        className={`gv-features-v3 gv-reveal${featuresR.shown ? ' in' : ''}`}
        aria-label="Three views of dealer flow"
      >
        {FEATURE_PILLARS.map(p => (
          <article key={p.num} className="gv-feat-v3">
            <span className={`gv-feat-num gv-feat-num--${p.tone}`}>{p.num}</span>
            <div>
              <div className="gv-feat-title">{p.title}</div>
              <div className="gv-feat-body">{p.body}</div>
            </div>
          </article>
        ))}
      </section>

      {/* ── Below-fold sections (preserved from prior landing) ── */}
      <section className="gv-section gv-section--alt gv-reveal-section">
        <div ref={timelineR.ref} className={`gv-section-inner gv-reveal${timelineR.shown ? ' in' : ''}`}>
          <h2 className="gv-section-title">How it works</h2>
          <ol className="gv-timeline">
            <li className="gv-step"><span className="gv-step-num">1</span><div><h4>Pull chains</h4><p>Options chains pulled every 30 s from Tradier (or Yahoo / mock).</p></div></li>
            <li className="gv-step"><span className="gv-step-num">2</span><div><h4>Compute GEX + VEX</h4><p>Per-strike dealer exposure via Black-Scholes greeks, dollar-notional units.</p></div></li>
            <li className="gv-step"><span className="gv-step-num">3</span><div><h4>Classify nodes</h4><p>King, Gatekeeper, Hedge — and the air pockets between them.</p></div></li>
            <li className="gv-step"><span className="gv-step-num">4</span><div><h4>Stream to your screen</h4><p>WebSocket frame in your browser ~200 ms after the chain settles.</p></div></li>
          </ol>
        </div>
      </section>

      <section className="gv-section gv-reveal-section">
        <div ref={proofR.ref} className={`gv-section-inner gv-proof-section gv-reveal${proofR.shown ? ' in' : ''}`}>
          <h2 className="gv-section-title">Traders who use it</h2>
          <div className="gv-proof-card">
            <ProofLine items={PROOF} />
          </div>
        </div>
      </section>

      <section className="gv-section gv-reveal-section">
        <div ref={pricingR.ref} className={`gv-section-inner gv-reveal${pricingR.shown ? ' in' : ''}`}>
          <h2 className="gv-section-title">Pricing</h2>
          <PricingStrip />
          <p style={{ textAlign: 'center', marginTop: '1rem' }}>
            <Link to="/pricing" className="gv-cta-secondary" style={{ display: 'inline-block' }}>See all plans →</Link>
          </p>
        </div>
      </section>

      <section className="gv-section gv-section--alt gv-reveal-section">
        <div ref={faqR.ref} className={`gv-section-inner gv-reveal${faqR.shown ? ' in' : ''}`}>
          <h2 className="gv-section-title">Frequently asked</h2>
          <div className="gv-faq" style={{ maxWidth: 720, margin: '0 auto' }}>
            {FAQ_TEASERS.map((f, i) => (
              <details key={i}>
                <summary>{f.q}</summary>
                <div>{f.a}</div>
              </details>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <Link to="/faq" className="gv-cta-secondary" style={{ display: 'inline-block' }}>See all FAQs →</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Landing;

/* ──────────────────────────────────────────────────────────────
   Hero data-panel mocks. Static SVGs because the public landing
   doesn't carry a websocket connection (no auth) — the in-product
   pages are where real-time wiring lives. The mock matches the
   visual character of the live app so visitors see the look they'll
   get behind the trial.
   ────────────────────────────────────────────────────────────── */

const HEAT_RAMP = [
  '#0a3d22', '#125a35', '#1d8050', '#2cb86a', '#3aff8a',
  '#a8e070', '#ffb000', '#ff7a1c', '#ff3838',
];

const HeroHeatmapMock: React.FC = () => {
  // 22 cols × 9 rows synthetic dealer-positioning shape — two attractor
  // basins (one positive, one negative) with mild noise. Sample by
  // index from HEAT_RAMP per the design handoff (no interpolation).
  const cols = 22, rows = 9, w = 540, h = 200;
  const cw = w / cols, ch = h / rows;
  const cells: { c: number; r: number; v: number }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cx1 = cols * 0.28, cy1 = rows * 0.4;
      const cx2 = cols * 0.72, cy2 = rows * 0.6;
      const d1 = Math.sqrt(((c - cx1) / 6) ** 2 + ((r - cy1) / 2.5) ** 2);
      const d2 = Math.sqrt(((c - cx2) / 5) ** 2 + ((r - cy2) / 3) ** 2);
      const v = Math.max(-1, Math.min(1,
        1.5 * Math.exp(-d1 * 1.1) - 1.0 * Math.exp(-d2 * 1.3)
        + Math.sin(c * 0.4 + r * 0.3) * 0.05));
      cells.push({ c, r, v });
    }
  }
  return (
    <div className="gv-hero-heatmap-card" aria-hidden="true">
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="200" role="img" aria-label="Sample dealer-flow heatmap">
        {cells.map((cell, i) => {
          const idx = Math.round((cell.v + 1) / 2 * (HEAT_RAMP.length - 1));
          const fill = HEAT_RAMP[idx];
          return (
            <rect
              key={i}
              x={cell.c * cw}
              y={cell.r * ch}
              width={cw - 1}
              height={ch - 1}
              fill={fill}
            />
          );
        })}
      </svg>
      <div className="gv-strike-axis">
        <span>5780</span><span>5800</span><span>5820</span>
        <span>5840</span><span>5860</span><span>5880</span>
      </div>
    </div>
  );
};

const Sparkline: React.FC<{ color: string; seed: number }> = ({ color, seed }) => {
  const sw = 84, sh = 22, n = 24;
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const v = Math.sin(i * 0.5 + seed) * 0.3
            + Math.cos(i * 0.3 + seed * 2) * 0.2
            + (i / n) * 0.4;
    pts.push(`${i * (sw / (n - 1))},${sh / 2 - v * sh * 0.4}`);
  }
  return (
    <svg width={sw} height={sh} aria-hidden="true">
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
};
